import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Tooltip,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Chip
} from '@mui/material';
import { HistoryOutlined, CloseOutlined, FileDownloadOutlined, RefreshOutlined, Search } from '@mui/icons-material';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { listBalanceAdjustmentsApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { formatINR, renderCrossbankingStatusChip } from './creditLimitsUtils';

const BalanceAdjustmentsHistoryModal = ({ open, onClose, initialAccount }) => {
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
    const [historyPerPage, setHistoryPerPage] = useState(20);
    const [historyFilters, setHistoryFilters] = useState({
        search: '',
        type: '',
        category: '',
        start_date: '',
        end_date: ''
    });

    const fetchBalanceAdjustments = useCallback(
        async (page = 1, perPage = 20, filters = historyFilters) => {
            setHistoryLoading(true);
            try {
                const payload = {
                    page,
                    per_page: perPage,
                    ...(filters.search?.trim() && { search: filters.search.trim() }),
                    ...(filters.type && { type: filters.type }),
                    ...(filters.category?.trim() && { category: filters.category.trim() }),
                    ...(filters.start_date && { start_date: filters.start_date }),
                    ...(filters.end_date && { end_date: filters.end_date })
                };

                const response = await listBalanceAdjustmentsApi(payload);
                if (response?.data?.success && response?.data?.data) {
                    const resData = response.data.data;
                    const items = Array.isArray(resData.data) ? resData.data : [];
                    setHistoryData(items);
                    setHistoryTotal(resData.total ?? items.length);
                    setHistoryCurrentPage(resData.current_page ?? page);
                    setHistoryPerPage(resData.per_page ?? perPage);
                } else if (response?.data?.message) {
                    setHistoryData([]);
                    setHistoryTotal(0);
                } else if (response?.response?.data?.message) {
                    setHistoryData([]);
                    setHistoryTotal(0);
                } else {
                    setHistoryData([]);
                    setHistoryTotal(0);
                }
            } catch (error) {
                console.error('Error fetching balance adjustments history:', error);
                handleCatchError(error);
                setHistoryData([]);
                setHistoryTotal(0);
            } finally {
                setHistoryLoading(false);
            }
        },
        [historyFilters]
    );

    useEffect(() => {
        if (open) {
            const initial = {
                search: initialAccount
                    ? initialAccount.email ||
                      initialAccount.employee_code ||
                      String(initialAccount.cross_user_id || initialAccount.id || '')
                    : '',
                type: '',
                category: '',
                start_date: '',
                end_date: ''
            };
            setHistoryFilters(initial);
            setHistoryCurrentPage(1);
            fetchBalanceAdjustments(1, historyPerPage, initial);
        }
    }, [open, initialAccount]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterChange = (field, value) => {
        setHistoryFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setHistoryCurrentPage(1);
        fetchBalanceAdjustments(1, historyPerPage, historyFilters);
    };

    const handleResetFilters = () => {
        const resetFilters = {
            search: '',
            type: '',
            category: '',
            start_date: '',
            end_date: ''
        };
        setHistoryFilters(resetFilters);
        setHistoryCurrentPage(1);
        fetchBalanceAdjustments(1, historyPerPage, resetFilters);
    };

    const handlePageChange = (newPage) => {
        setHistoryCurrentPage(newPage);
        fetchBalanceAdjustments(newPage, historyPerPage, historyFilters);
    };

    const handlePerPageChange = (newPerPage) => {
        setHistoryPerPage(newPerPage);
        setHistoryCurrentPage(1);
        fetchBalanceAdjustments(1, newPerPage, historyFilters);
    };

    const handleExportHistoryToExcel = () => {
        if (!historyData || historyData.length === 0) {
            globalNotifyError('No history records available to export');
            return;
        }

        const exportRows = historyData.map((item) => ({
            'Log ID': item.id,
            'Date & Time': item.created_at ? moment(item.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
            'Official Emp ID': item.official_emp_id || '',
            'Employee Name': item.cross_user_name || '',
            Email: item.email || '',
            Phone: item.cross_user_phone || '',
            'Adjustment Type': item.type ? item.type.toUpperCase() : '',
            Amount: Number(item.amount) || 0,
            Category: item.category || '',
            Remarks: item.remarks || '',
            'Crossbanking Status': item.crossbanking_status ?? ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance_Adjustments');
        XLSX.writeFile(workbook, `Balance_Adjustments_History_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                    minHeight: 620,
                    maxHeight: '92vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#e0f2fe', color: '#0284c7', display: 'flex' }}>
                        <HistoryOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Balance Adjustments History
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Audit logs of all HR balance credits and debits
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Export History to Excel">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlined fontSize="small" />}
                                onClick={handleExportHistoryToExcel}
                                disabled={historyLoading || historyData.length === 0}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                            >
                                Export Excel
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Refresh History">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => fetchBalanceAdjustments(historyCurrentPage, historyPerPage, historyFilters)}
                                disabled={historyLoading}
                            >
                                <RefreshOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose}>
                        <CloseOutlined fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* Filters Toolbar */}
                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2.5,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0'
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                            {/* Search */}
                            <TextField
                                size="small"
                                placeholder="Search name, email, employee code, remarks..."
                                value={historyFilters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flex: 2, minWidth: 220, bgcolor: '#fff', borderRadius: 1.5 }}
                            />

                            {/* Type Filter */}
                            <FormControl size="small" sx={{ minWidth: 130, bgcolor: '#fff', borderRadius: 1.5 }}>
                                <InputLabel id="history-type-label">Type</InputLabel>
                                <Select
                                    labelId="history-type-label"
                                    label="Type"
                                    value={historyFilters.type}
                                    onChange={(e) => handleFilterChange('type', e.target.value)}
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    <MenuItem value="credit">Credit</MenuItem>
                                    <MenuItem value="debit">Debit</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Category Filter */}
                            <FormControl size="small" sx={{ minWidth: 150, bgcolor: '#fff', borderRadius: 1.5 }}>
                                <InputLabel id="history-category-label">Category</InputLabel>
                                <Select
                                    labelId="history-category-label"
                                    label="Category"
                                    value={historyFilters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    <MenuItem value="TA">TA</MenuItem>
                                    <MenuItem value="Bonus">Bonus</MenuItem>
                                    <MenuItem value="Allowance">Allowance</MenuItem>
                                    <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                                    <MenuItem value="TA Deduction">TA Deduction</MenuItem>
                                    <MenuItem value="Salary Advance">Salary Advance</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Date Range */}
                            <TextField
                                size="small"
                                type="date"
                                label="From Date"
                                InputLabelProps={{ shrink: true }}
                                value={historyFilters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                sx={{ minWidth: 135, bgcolor: '#fff', borderRadius: 1.5 }}
                            />

                            <TextField
                                size="small"
                                type="date"
                                label="To Date"
                                InputLabelProps={{ shrink: true }}
                                value={historyFilters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                sx={{ minWidth: 135, bgcolor: '#fff', borderRadius: 1.5 }}
                            />

                            {/* Buttons */}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleApplyFilters}
                                    disabled={historyLoading}
                                    sx={{
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2,
                                        bgcolor: '#0284c7',
                                        '&:hover': { bgcolor: '#0369a1' }
                                    }}
                                >
                                    Filter
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleResetFilters}
                                    disabled={historyLoading}
                                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>

                {/* Table Container */}
                <TableContainer
                    sx={{
                        flex: 1,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#fff',
                        maxHeight: 440,
                        overflowY: 'auto'
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }}>Date & Time</TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }}>Employee</TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }} align="center">
                                    Type
                                </TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }} align="right">
                                    Amount
                                </TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }}>Category</TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }}>Remarks</TableCell>
                                <TableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }} align="center">
                                    Crossbanking Status
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {historyLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                            Loading balance adjustments...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : historyData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ p: 2, color: 'text.secondary' }}>
                                            <HistoryOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                                No balance adjustment logs found
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                                Try adjusting your search criteria or date filters.
                                            </Typography>
                                            {/* <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={handleResetFilters}
                                                sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                            >
                                                Clear All Filters
                                            </Button> */}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                historyData.map((item, index) => {
                                    const isCredit = String(item.type || '').toLowerCase() === 'credit';
                                    return (
                                        <TableRow
                                            key={item.id || index}
                                            hover
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                transition: 'background-color 0.15s ease'
                                            }}
                                        >
                                            {/* Date & Time */}
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                                    {item.created_at ? moment(item.created_at).format('DD MMM YYYY') : '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.created_at ? moment(item.created_at).format('hh:mm A') : ''}
                                                </Typography>
                                            </TableCell>

                                            {/* Employee */}
                                            <TableCell>
                                                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                                                    {item.cross_user_name || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {item.email || '-'}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                                    {item.official_emp_id && (
                                                        <Chip
                                                            size="small"
                                                            label={item.official_emp_id}
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                                bgcolor: '#eff6ff',
                                                                color: '#1d4ed8'
                                                            }}
                                                        />
                                                    )}
                                                    {item.cross_user_phone && (
                                                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                                            {item.cross_user_phone}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>

                                            {/* Type */}
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={isCredit ? '+ CREDIT' : '- DEBIT'}
                                                    sx={{
                                                        height: 22,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        bgcolor: isCredit ? '#f0fdf4' : '#fef2f2',
                                                        color: isCredit ? '#15803d' : '#b91c1c',
                                                        border: `1px solid ${isCredit ? '#bbf7d0' : '#fecaca'}`
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Amount */}
                                            <TableCell align="right">
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={700}
                                                    sx={{ color: isCredit ? '#15803d' : '#dc2626' }}
                                                >
                                                    {isCredit ? '+ ' : '- '}
                                                    {formatINR(item.amount)}
                                                </Typography>
                                            </TableCell>

                                            {/* Category */}
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={item.category || 'General'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        bgcolor: '#f1f5f9',
                                                        color: '#475569'
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Remarks */}
                                            <TableCell>
                                                <Tooltip title={item.remarks || ''}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            maxWidth: 220,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {item.remarks || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>

                                            {/* Crossbanking Status */}
                                            <TableCell align="center">{renderCrossbankingStatusChip(item.crossbanking_status)}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={historyTotal}
                    page={Math.max(0, historyCurrentPage - 1)}
                    onPageChange={(e, newPage) => handlePageChange(newPage + 1)}
                    rowsPerPage={historyPerPage}
                    onRowsPerPageChange={(e) => handlePerPageChange(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    sx={{ mt: 1, borderTop: '1px solid #e2e8f0' }}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 1.5 }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

BalanceAdjustmentsHistoryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialAccount: PropTypes.object
};

export default BalanceAdjustmentsHistoryModal;
